import { useState, useEffect, useRef } from "react";
import html2pdf from "html2pdf.js";
import './dailyexpense.css';

const DailyExpenses = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const [expenses, setExpenses] = useState([]);
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [description, setDescription] = useState('');
  const [otherCategory, setOtherCategory] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [driverAllowances, setDriverAllowances] = useState([{ name: '', amount: '' }]);

  const pdfRef = useRef();

  useEffect(() => {
    fetchExpenses();
  }, [selectedDate]);

  useEffect(() => {
    if (expenseCategory !== 'Employee Allowance') {
      setDriverAllowances([{ name: '', amount: '' }]);
    }
  }, [expenseCategory]);

  const fetchExpenses = async () => {
    const res = await fetch(`https://kmtea.onrender.com/expenses?date=${selectedDate}`);
    const data = await res.json();
    setExpenses(data);
    calculateTotal(data);
  };

  const calculateTotal = (expenses) => {
    const total = expenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
    setTotalExpenses(total);
  };

  const handleDriverAllowanceChange = (index, field, value) => {
    const updated = [...driverAllowances];
    updated[index][field] = value;
    setDriverAllowances(updated);
  };

  const addDriverAllowanceRow = () => {
    setDriverAllowances([...driverAllowances, { name: '', amount: '' }]);
  };

  const removeDriverAllowanceRow = (index) => {
    const updated = [...driverAllowances];
    updated.splice(index, 1);
    setDriverAllowances(updated);
  };

  const handleAddOrUpdateExpense = async () => {
    if (!expenseCategory) return alert('Please select category.');

    if (expenseCategory === 'Employee Allowance') {
      for (const entry of driverAllowances) {
        if (!entry.name || !entry.amount) return alert('Fill all employee allowance fields.');
      }
    } else if (!expenseAmount) {
      return alert('Please enter an amount.');
    }

    if (editIndex !== null) {
      const payload = {
        date: selectedDate,
        category: expenseCategory === 'Others' ? otherCategory : expenseCategory,
        amount: parseFloat(expenseAmount),
        description,
      };

      const res = await fetch(`https://kmtea.onrender.com/expenses/${expenses[editIndex].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const updated = [...expenses];
        updated[editIndex] = { ...updated[editIndex], ...payload };
        setExpenses(updated);
        calculateTotal(updated);
        setEditIndex(null);
      }
    } else {
      if (expenseCategory === 'Employee Allowance') {
        const newItems = [];

        for (const entry of driverAllowances) {
          const payload = {
            date: selectedDate,
            category: 'Employee Allowance',
            amount: parseFloat(entry.amount),
            description: entry.name,
          };
          const res = await fetch('https://kmtea.onrender.com/expenses', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (res.ok) newItems.push(await res.json());
        }

        const updated = [...expenses, ...newItems];
        setExpenses(updated);
        calculateTotal(updated);
      } else {
        const payload = {
          date: selectedDate,
          category: expenseCategory === 'Others' ? otherCategory : expenseCategory,
          amount: parseFloat(expenseAmount),
          description,
        };

        const res = await fetch('https://kmtea.onrender.com/expenses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          const newItem = await res.json();
          const updated = [...expenses, newItem];
          setExpenses(updated);
          calculateTotal(updated);
        }
      }
    }

    setExpenseCategory('');
    setExpenseAmount('');
    setDescription('');
    setOtherCategory('');
    setEditIndex(null);
    setDriverAllowances([{ name: '', amount: '' }]);
  };

  const handleEdit = (index) => {
    const item = expenses[index];
    setExpenseCategory(
      ['Petrol Allowance', 'Tea Allowance', 'Employee Allowance'].includes(item.category)
        ? item.category
        : 'Others'
    );
    setOtherCategory(item.category === 'Others' ? item.category : '');
    setDescription(item.description || '');
    if (item.category === 'Employee Allowance') {
      setDriverAllowances([{ name: item.description, amount: item.amount }]);
    } else {
      setExpenseAmount(item.amount);
    }
    setEditIndex(index);
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Are you sure?")) return;
    const id = expenses[index].id;
    const res = await fetch(`https://kmtea.onrender.com/expenses/${id}`, { method: 'DELETE' });
    if (res.ok) {
      const updated = expenses.filter((_, i) => i !== index);
      setExpenses(updated);
      calculateTotal(updated);
    }
  };

  const handleHtml2PdfExport = () => {
    html2pdf().set({
      margin: 0.5,
      filename: `daily_expenses_${selectedDate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    }).from(pdfRef.current).save();
  };

  return (
    <div className="daily-expense-container">
      <div ref={pdfRef}>
        <h2 className="daily-expense-heading">Daily Expenses</h2>

        <div className="daily-expense-form-row">
          <label>Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="daily-expense-form-vertical">
          <div className="daily-expense-form-row">
            <div>
              <label>Category:</label>
              <select
                value={expenseCategory}
                onChange={(e) => setExpenseCategory(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="Petrol Allowance">Petrol Allowance</option>
                <option value="Tea Allowance">Tea Allowance</option>
                <option value="Employee Allowance">Employee Allowance</option>
                <option value="Others">Others</option>
              </select>
            </div>

            {expenseCategory === 'Employee Allowance' && (
              <div>
                <label>Employee Allowances:</label>
                {driverAllowances.map((entry, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Name"
                      value={entry.name}
                      onChange={(e) => handleDriverAllowanceChange(index, 'name', e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={entry.amount}
                      onChange={(e) => handleDriverAllowanceChange(index, 'amount', e.target.value)}
                    />
                    {driverAllowances.length > 1 && (
                      <button type="button" onClick={() => removeDriverAllowanceRow(index)}>❌</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addDriverAllowanceRow}>＋ Add More</button>
              </div>
            )}

            {expenseCategory !== 'Employee Allowance' && (
              <div>
                <label>Amount:</label>
                <input
                  type="number"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                />
              </div>
            )}

            <div>
              <label>Description:</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          {expenseCategory === 'Others' && (
            <div className="daily-expense-form-row">
              <label>Other Category:</label>
              <input
                type="text"
                value={otherCategory}
                onChange={(e) => setOtherCategory(e.target.value)}
              />
            </div>
          )}

          <div className="daily-expense-form-row">
            <button onClick={handleAddOrUpdateExpense}>
              {editIndex !== null ? 'Update Expense' : 'Add Expense'}
            </button>
          </div>
        </div>

        <table className="daily-expense-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Amount (₹)</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr>
                <td colSpan="5">No expenses for this date.</td>
              </tr>
            ) : (
              expenses.map((expense, index) => (
                <tr key={expense.id || index}>
                  <td>{new Date(expense.date).toLocaleDateString('en-GB')}</td>
                  <td>{expense.category}</td>
                  <td>{parseFloat(expense.amount).toFixed(2)}</td>
                  <td>{expense.description || '-'}</td>
                  <td>
                    <button onClick={() => handleEdit(index)}>✏️</button>
                    <button onClick={() => handleDelete(index)}>🗑️</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="daily-expense-total">
          Total Expense: ₹{totalExpenses.toFixed(2)}
        </div>
      </div>

      <button onClick={handleHtml2PdfExport}>📄</button>
    </div>
  );
};

export default DailyExpenses;
